<template>
	<div class="amelipro-accordion-list">
		<div
			v-if="!noResultListInfos"
			class="d-flex justify-space-between align-end mb-6"
		>
			<p class="d-inline-block mb-0 total-counter font-weight-semibold">
				<span class="d-sr-only">Nombre de ligne dans la liste ci-après :</span>
				{{ items.length }} {{ counterLabel }}
			</p>

			<div class="d-inline-flex align-center">
				<AmeliproSelect
					v-if="sortSelectItems.length > 0"
					:id="`${uniqueId}-sort-select`"
					ref="ameliproSortSelect"
					class="ml-2 amelipro-accordion-list__sort-select"
					global-max-width="170px"
					hide-error-message
					:items="sortSelectItems"
					label="Trier par"
					placeholder="Trier par :"
					style="padding-top: 0 !important;"
					@change="emitSortSelectChange()"
				/>

				<AmeliproSelect
					v-if="breakpointHelper.isDesktop()"
					:id="`${uniqueId}-pagination-select`"
					ref="ameliproPaginationSelect"
					class="ml-2 amelipro-accordion-list__pagination-select"
					global-max-width="170px"
					hide-error-message
					:items="paginationSelectItems"
					label="Nb lignes/page :"
					placeholder="Nb lignes/page"
					style="padding-top: 0 !important;"
					@change="emitPaginationSelectChange()"
				/>
			</div>
		</div>
		<div>
			<ul
				:aria-label="title"
				class="list-style-none amelipro-accordion-list__list"
			>
				<li
					v-for="(accordion, index) in currentPageItems(items)"
					:key="index"
					class="amelipro-accordion-list__item"
				>
					<AmeliproAccordionTemplate
						:accordion-title="String(accordion.accordionTitle)"
						:border-color="groupBorderColor"
						:bordered="groupBordered"
						:card-color="groupColor"
						:header-right-width="String(accordion.headerRightWidth)"
						:is-open="`accordion-result-${accordion.id}` === openId"
						:title-level="titleLevel"
						:title-uppercase="titleUppercase"
						:unique-id="`accordion-result-${accordion.id}`"
						@open-close="openClose"
					>
						<template #headerRight>
							<slot
								:name="`headingContent-${accordion.id}`"
								v-bind="accordion"
							>
								<slot
									name="headingContent"
									v-bind="accordion"
								/>
							</slot>
						</template>

						<template #default>
							<slot
								:name="`accordionContent-${accordion.id}`"
								v-bind="accordion"
							>
								<slot
									name="accordionContent"
									v-bind="accordion"
								/>
							</slot>
						</template>
					</AmeliproAccordionTemplate>
				</li>
			</ul>
		</div>

		<AmeliproPagination
			v-if="paginationTable !== undefined && paginationTable.length > 0"
			:active-page-default="1"
			class="mt-2"
			:items="paginationTable"
			@click="emitClickEvent"
		/>
	</div>
</template>

<script setup lang="ts">
	import { AmeliproPaginationSelect, AmeliproSortSelect } from './types';
	import { PropType, onMounted, onUpdated, ref, watch } from 'vue';
	import AmeliproAccordionTemplate from '../AmeliproAccordionTemplate/AmeliproAccordionTemplate.vue';
	import AmeliproPagination from '../AmeliproPagination/AmeliproPagination.vue';
	import AmeliproSelect from '../AmeliproSelect/AmeliproSelect.vue';
	import BreakpointHelper from '../../helpers/BreakpointHelper';
	import { IDataListItem } from '../../types';
	import { SelectItem } from '../AmeliproSelect/types';
	import { usePagination } from '../../composables/usePagination';

	const props = defineProps({
		counterLabel: {
			type: String,
			default: 'résultat(s)',
		},
		defaultItemOpened: {
			type: [Number, null] as PropType<number | null>,
			default: null,
		},
		groupBorderColor: {
			type: String,
			default: 'ap-grey lighten-2',
		},
		groupBordered: {
			type: Boolean,
			default: true,
		},
		groupColor: {
			type: String,
			default: 'ap-white',
		},
		hiddenLabels: {
			type: Boolean,
			default: false,
		},
		items: {
			type: Array as PropType<IDataListItem[]>,
			default: () => [],
		},
		itemsToDisplayDesktop: {
			type: Number,
			default: 10,
		},
		itemsToDisplayMobile: {
			type: Number,
			default: 10,
		},
		noResultListInfos: {
			type: Boolean,
			default: false,
		},
		sortSelectItems: {
			type: Array as PropType<SelectItem[]>,
			default: () => [],
		},
		title: {
			type: String,
			required: true,
		},
		titleLevel: {
			type: Number,
			default: 2,
		},
		titleUppercase: {
			type: Boolean,
			default: false,
		},
		uniqueId: {
			type: String,
			required: true,
		},
	});

	const ameliproPaginationSelect = ref<AmeliproPaginationSelect>();
	const ameliproSortSelect = ref<AmeliproSortSelect>();
	const openId = ref<string | null>(null);
	const breakpointHelper = new BreakpointHelper();

	const {
		currentPage,
		currentPageItems,
		itemToDisplay,
		paginationSelectItems,
		paginationTable,
		setDefaultItemsPerPage,
		updatePagination,
	} = usePagination(props.items, props.itemsToDisplayDesktop, props.itemsToDisplayMobile);

	onMounted(() => {
		if (props.defaultItemOpened !== null) {
			openId.value = String(props.items[props.defaultItemOpened].id);
		}
		hideSelectLabel();
		setDefaultItemsPerPage();
	});

	onUpdated(() => {
		hideSelectLabel();
	});

	watch(() => props.items, () => {
		updatePagination(props.items, itemToDisplay.value);
	});

	const openClose = (id: number): void => {
		openId.value = openId.value === String(id) ? null : String(id);
	};

	const hideSelectLabel = (): void => {
		if (props.hiddenLabels) {
			document.getElementById(`${props.uniqueId}-sort-select-label`)?.setAttribute('class', 'd-sr-only');
			document.getElementById(`${props.uniqueId}-pagination-select-label`)?.setAttribute('class', 'd-sr-only');
		}
	};

	const emit = defineEmits(['click', 'change:sort-select', 'change:pagination-select']);
	const emitClickEvent = (newCurrentPage: number): void => {
		emit('click');
		if (newCurrentPage !== null && newCurrentPage !== undefined) {
			currentPage.value = newCurrentPage;
		}
	};

	const emitSortSelectChange = (): void => {
		emit('change:sort-select', ameliproSortSelect.value?.inputValue);
	};

	const emitPaginationSelectChange = (): void => {
		emit('change:pagination-select');
		if (ameliproPaginationSelect.value && (ameliproPaginationSelect.value.inputValue !== null || ameliproPaginationSelect.value.inputValue !== undefined)) {
			updatePagination(props.items, ameliproPaginationSelect.value.inputValue);
		}

		if ((currentPage.value !== null && currentPage.value !== undefined) && currentPage.value > paginationTable.value.length) {
			currentPage.value = paginationTable.value.length;
		}

		if (currentPage.value === 0) {
			currentPage.value = 1;
		}
	};

	// Rendre publique la méthode openClose permet à un bouton ou à un composant externe de fermer/ouvrir l'accordéon
	defineExpose({ openClose });

</script>

<style lang="scss" scoped>
.total-counter {
	background-color: $ap-grey-lighten2;
	border: 1px solid $ap-grey-darken1;
	border-radius: $btn-radius;
	padding: 0.75rem 1rem;
}
</style>
